import { useEffect, useState } from "react";
import { AlertTriangle, Leaf, Sparkles, History, Info, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STYLE = {
  warning: { icon: AlertTriangle, cls: "border-rose-200 bg-rose-50 text-rose-700", label: "Rising" },
  seasonal: { icon: Leaf, cls: "border-emerald-200 bg-emerald-50 text-emerald-700", label: "In season" },
  opportunity: { icon: Sparkles, cls: "border-sky-200 bg-sky-50 text-sky-700", label: "Opportunity" },
  last_year: { icon: History, cls: "border-amber-200 bg-amber-50 text-amber-700", label: "Last year" },
  info: { icon: Info, cls: "border-slate-200 bg-slate-50 text-slate-600", label: "Note" },
};

export default function MarketNotes({ restaurantId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = (refresh = false) => {
    setBusy(true);
    return api.get("/insights/market-notes", { params: { restaurant_id: restaurantId || undefined, refresh: refresh || undefined } })
      .then((r) => {
        setData(r.data);
        if (r.data.generating) setTimeout(() => load(), 4000);
        if (refresh && r.data.refresh_blocked) toast.info("This week's notes were already refreshed today — try again tomorrow.");
      })
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { setData(null); load(); }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const notes = data?.notes || [];
  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="market-notes">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <h2 className="text-base md:text-lg font-semibold text-slate-900">Market notes</h2>
          <p className="text-xs text-slate-500">Seasonal tips & risks for what you buy · written once a week</p>
        </div>
        {user?.role === "owner" && <button onClick={() => load(true)} disabled={busy} className="p-2 rounded-md text-slate-400 hover:text-emerald-700 hover:bg-slate-50 disabled:opacity-50" title="Regenerate (once a day)" data-testid="market-notes-refresh">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
        </button>}
      </div>
      <div className="p-3 space-y-2">
        {!data || data.generating ? <p className="px-1 py-4 text-sm text-slate-500" data-testid="market-notes-loading">{data?.generating ? "Writing this week's notes…" : "Loading…"}</p>
          : data.empty ? <p className="px-1 py-4 text-sm text-slate-500" data-testid="market-notes-empty">Confirm a few invoices first — notes are written around the products you actually buy.</p>
          : data.error ? <p className="px-1 py-4 text-sm text-rose-600" data-testid="market-notes-error">{data.error}</p>
          : notes.map((n, i) => {
            const s = STYLE[n.type] || STYLE.info;
            const Icon = s.icon;
            return (
              <article key={i} className={`rounded-md border px-3 py-2.5 ${s.cls}`} data-testid={`market-note-${i}`}>
                <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider"><Icon className="h-3.5 w-3.5" /> {s.label}</div>
                <p className="mt-1 text-sm font-semibold text-slate-900">{n.title}</p>
                <p className="text-sm text-slate-700 leading-snug">{n.text}</p>
                {n.products?.length > 0 && <p className="mt-1 text-[11px] text-slate-500 truncate">{n.products.join(" · ")}</p>}
              </article>
            );
          })}
      </div>
      <p className="px-4 pb-3 text-[11px] text-slate-400">AI-written from your invoice data plus general UK market knowledge — not live market prices.</p>
    </section>
  );
}
